import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {useIssueState} from '@github-ui/issue-pr-state'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLBdi} from '@github-ui/safe-html/VerifiedHTML'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {LockIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {Octicon, Tooltip} from '@primer/react/deprecated'
import {clsx} from 'clsx'

import {LABELS} from '../constants/labels'
import {formatIssueReference} from '../utils/format-issue-reference'
import type {IssueLinkIssueNode, IssueLinkPullRequestNode} from './issue-viewer-view-query-types'
import styles from './IssueLink.module.css'

type IssueLinkRepository = Pick<IssueLinkIssueNode['repository'], 'id' | 'name' | 'isPrivate'> & {
  readonly owner: Pick<IssueLinkIssueNode['repository']['owner'], 'login'>
}

// RELAY-CLEANUP(BlockedByAddedEvent, BlockedByRemovedEvent, BlockingAddedEvent, BlockingRemovedEvent,
//   ClosedEvent, CrossReferencedEvent, MarkedAsDuplicateEvent, UnmarkedAsDuplicateEvent,
//   ParentIssueAddedEvent, ParentIssueRemovedEvent, SubIssueAddedEvent, SubIssueRemovedEvent):
//   widens nullable fields so still-Relay parent data is assignable.
//   Remove when all listed parent fragments leave Relay.
export type IssueLinkFragment = RelayCompat<
  | (Pick<IssueLinkIssueNode, '__typename' | 'id' | 'url' | 'number' | 'stateReason'> & {
      readonly titleHTML: IssueLinkIssueNode['titleHTML']
      readonly repository: IssueLinkRepository
    })
  | (Pick<
      IssueLinkPullRequestNode,
      '__typename' | 'id' | 'pullTitleHTML' | 'url' | 'number' | 'state' | 'isDraft' | 'isInMergeQueue'
    > & {readonly repository: IssueLinkRepository})
>

type IssueLinkProps = {
  data: IssueLinkFragment
  targetOwnerLogin: string
  targetRepositoryId: string
  inline?: boolean
}

export function IssueLink({
  data: source,
  targetRepositoryId,
  targetOwnerLogin,
  inline = false,
}: IssueLinkProps): React.ReactElement {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const isIssue = source.__typename === 'Issue'
  const isPullRequest = source.__typename === 'PullRequest'

  const state = isPullRequest ? source.state : undefined
  const stateReason = isIssue ? source.stateReason : undefined
  const {sourceIcon} = useIssueState({state, stateReason})
  const isDraft = isPullRequest ? source.isDraft : undefined
  const isInMergeQueue = isPullRequest ? source.isInMergeQueue : undefined
  const icon = sourceIcon(source.__typename, isDraft, isInMergeQueue)

  if (!isIssue && !isPullRequest) {
    return <></>
  }
  const hovercardAttributes =
    source.repository && source.number
      ? isIssue
        ? getHovercardAttributesForType('issue', {
            owner: source.repository.owner.login,
            repo: source.repository.name,
            issueNumber: source.number,
          })
        : getHovercardAttributesForType('pull_request', {
            owner: source.repository.owner.login,
            repo: source.repository.name,
            pullRequestNumber: source.number,
          })
      : {}

  const issueReference = formatIssueReference(
    {
      number: source.number,
      ownerLogin: source.repository.owner.login,
      repoId: source.repository.id,
      repoName: source.repository.name,
    },
    targetRepositoryId,
    targetOwnerLogin,
  )

  const privateDescriptionId = `${source.id}-private-description`

  // Defining the title in this way avoids a problem that stems from the titleHTML field being defined as different
  // types in the Issue and PullRequest platform objects.
  const titleHTML = isIssue ? source.titleHTML : source.pullTitleHTML

  const InlineLink = (
    <>
      <Octicon icon={icon} size={16} className={clsx(inline ? styles.inlineIcon : styles.blockIcon)} />
      <Link
        href={source.url}
        {...hovercardAttributes}
        aria-describedby={source.repository.isPrivate ? privateDescriptionId : undefined}
        inline
        className={styles.issueLinkAnchor}
      >
        <VerifiedHTMLBdi
          className={clsx('markdown-title', styles.issueTitleContainer)}
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to the IssueViewerViewQuery SPQ field (titleHTML / pullTitleHTML) once the type is marked as HTML. */
          html={titleHTML as SafeHTMLString}
        />
        <span className={styles.repoIssueNumber}> {issueReference}</span>
      </Link>
      {source.repository.isPrivate && source.repository.id !== targetRepositoryId && (
        <>
          <Tooltip
            aria-label={LABELS.crossReferencedEventLockTooltip(
              `${source.repository.owner.login}/${source.repository.name}`,
            )}
          >
            <Octicon icon={LockIcon} />
          </Tooltip>
          <span id={privateDescriptionId} className="sr-only">
            {LABELS.crossReferencedEvent.privateEventDescription}
          </span>
        </>
      )}
    </>
  )

  return inline ? <>{InlineLink}</> : <div className={styles.issueContainer}>{InlineLink}</div>
}
